import React, { FC, useState, useEffect } from 'react';
import { ColumnsType } from 'antd/lib/table';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import {
  Account,
  InvoiceStatus,
  InvoiceType,
  RevenueOperationType,
  Transaction,
  TransactionService
} from '../../../../swagger/api';
import { BrobizzTable } from '../../../../common-components/BrobizzTable';
import { ManchetContentCamptonBook22Grey } from '../../../../layout/globalFonts';
import ContentStore from '../../../../stores/ContentStore';
import useIsMobileView from '../../../../hooks/useIsMobileView';
import AgreementsTableMobile from './AgreementsTableMobile';
import LoadingSpinner from '../../../../common-components/LoadingSpinner';
import dateFormatting from '../../../../utils/dateFormatting';
import decimalFormatting from '../../../../utils/decimalFormatting';
import { Typography } from '../../../../layout/components/Typography';
import { ColorScheme } from '../../../../layout/colorScheme';
import { AgreementDisplayRow } from './AgreementTypes';

const TARGET_TOLL_CHARGERS = ['Ruter fra Öresundslinjen', 'Øresundsbron'] as const;

type TargetTollCharger = typeof TARGET_TOLL_CHARGERS[number];

function isAgreementTransaction(t: Transaction): boolean {
  return (
    t.invoiceStatus === InvoiceStatus.Paid &&
    t.invoiceType === InvoiceType.Invoice &&
    t.revenueOperationType === RevenueOperationType.Credit
  );
}

const StyledBrobizzTableForPrivate = styled(BrobizzTable)<{ dataSource?: AgreementDisplayRow[] }>`
  table colgroup {
    display: none !important;
  }

  table col {
    display: none !important;
    width: auto !important;
  }

  table {
    table-layout: auto !important;
  }

  table .ant-table-tbody > tr.ant-table-row:hover > td {
    cursor: default !important;
    background: inherit !important;
  }

  .ant-table-tbody > tr:nth-child(odd) > td {
    background-color: #f7f7f7 !important;
    border: none !important;
  }

  .ant-table-tbody > tr:nth-child(even) > td {
    background-color: white !important;
    border: none !important;
  }

  .ant-table-tbody > tr {
    cursor: default !important;
    border-top: none !important;
  }

  .ant-table-tbody > tr:hover > td {
    background-color: inherit !important;
  }

  .ant-table-container {
    border: none !important;
  }

  .ant-table {
    border: none !important;
  }

  thead.ant-table-thead,
  tbody.ant-table-tbody {
    display: contents !important;
  }

  thead.ant-table-thead > tr,
  tbody.ant-table-tbody > tr:not(.ant-table-placeholder),
  .ant-table-thead > tr,
  .ant-table-tbody > tr:not(.ant-table-placeholder) {
    display: grid !important;
    grid-template-columns: 0.9fr 1.5fr 1.7fr 1.3fr 1fr 1.1fr !important;
    @media (min-width: 1380px) {
      grid-template-columns: 1.5fr 1.5fr 2.1fr 1.4fr 1fr 1.2fr !important;
    }
    align-items: center !important;
    width: 100% !important;
  }

  .ant-table-thead > tr {
    min-height: 70px !important;
  }

  .ant-table-tbody > tr > td {
    display: flex !important;
    align-items: center !important;
    overflow: hidden;
    min-width: 0;
    height: 100% !important;
  }

  .ant-table-thead > tr > th {
    display: flex !important;
    align-items: center !important;
    overflow: hidden;
    min-width: 0;
    min-height: 70px !important;
    box-sizing: border-box;
    justify-content: flex-start !important;
    border: none !important;
  }

  .ant-table-thead > tr > th:nth-child(5) {
    justify-content: flex-end !important;
  }

  .ant-table-tbody > tr > td:nth-child(5) {
    justify-content: flex-end !important;
  }

  .ant-table-thead > tr > th:first-child,
  .ant-table-tbody > tr:not(.ant-table-placeholder) > td:first-child {
    padding-left: 40px !important;
  }

  .ant-table-thead > tr > th:last-child,
  .ant-table-tbody > tr:not(.ant-table-placeholder) > td:last-child {
    padding-right: 40px !important;
    justify-content: flex-end !important;
  }

  .ant-table-tbody > tr:first-child,
  .ant-table-tbody > tr:first-child > td {
    border-top: none !important;
  }

  .ant-table-cell {
    p {
      margin: 0;
    }
  }

  .ant-table-tbody > tr.ant-table-placeholder {
    display: grid !important;
    grid-template-columns: 1fr !important;

    > td {
      grid-column: 1;
      width: 100%;
    }
  }
`;

const StyledText = styled(ManchetContentCamptonBook22Grey)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 168px;

  @media (max-width: 768px) {
    padding: 22px 20% 0px;
    text-align: center;
    font-family: Campton Book;
    -webkit-font-smoothing: subpixel-antialiased;
    -moz-osx-font-smoothing: auto;
    font-size: 19px;
    font-weight: 400;
    line-height: 1.5715;
    height: auto;
  }
`;

interface AgreementsTableProps {
  currentAccount: Account | null | undefined;
  contentStore: ContentStore;
}

const AgreementsTable: FC<AgreementsTableProps> = ({ currentAccount, contentStore }) => {
  const isMobileView = useIsMobileView();
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState<AgreementDisplayRow[]>([]);

  useEffect(() => {
    let isCancelled = false;

    const fetchAllTransactions = async () => {
      try {
        if (!currentAccount?.id) {
          setRows([]);
          setIsLoading(false);
          return;
        }

        setIsLoading(true);

        const allTransactions: Transaction[] = [];
        let pageNumber = 1;
        let hasMorePages = true;

        // eslint-disable-next-line no-await-in-loop
        while (hasMorePages) {
          // eslint-disable-next-line no-await-in-loop
          const response = await TransactionService.getTransactionsByAccountIdAsync({
            accountId: currentAccount.id,
            pageNumber
          });

          if (isCancelled) {
            return;
          }

          const pageItems = response.paginatedItems ?? [];
          allTransactions.push(...pageItems);

          const totalCount = response.totalCount ?? 0;
          const pageSize = response.pageSize ?? 20;
          const totalPages = Math.ceil(totalCount / pageSize);

          hasMorePages = pageNumber < totalPages && pageItems.length > 0;
          pageNumber += 1;
        }

        const filtered = allTransactions.filter((transaction) => {
          const hasDescription = transaction.description?.toLowerCase().includes('årsgebyr');
          const hasTollCharger =
            transaction.tollChargerName &&
            TARGET_TOLL_CHARGERS.includes(transaction.tollChargerName as TargetTollCharger);
          return hasDescription && hasTollCharger && isAgreementTransaction(transaction);
        });

        filtered.sort((a, b) => {
          const dateA = new Date(a.date ?? 0).getTime();
          const dateB = new Date(b.date ?? 0).getTime();
          return dateB - dateA;
        });

        const seenIdentifiers = new Set<string>();
        const displayRows: AgreementDisplayRow[] = filtered
          .filter((transaction) => {
            const key = transaction.identifierNumber || transaction.id;
            if (!key || seenIdentifiers.has(key)) {
              return false;
            }
            seenIdentifiers.add(key);
            return true;
          })
          .map((transaction) => {
            const dateLabel = transaction.date ? dateFormatting(transaction.date) : '-';
            const currencyCode = transaction.currency || currentAccount?.currency || 'DKK';

            return {
              key:
                transaction.id || transaction.identifierNumber || transaction.tollChargerName || '',
              nextPayment: dateLabel,
              description: transaction.description ?? '-',
              amount: Math.abs(transaction.amount ?? 0),
              currency: currencyCode
            };
          });

        if (!isCancelled) {
          setRows(displayRows);
        }
      } catch (error) {
        if (!isCancelled) {
          setRows([]);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchAllTransactions();

    return () => {
      isCancelled = true;
    };
  }, [currentAccount]);

  const agreementColumns: ColumnsType<AgreementDisplayRow> = [
    {
      title: contentStore.cmsStore.getContent('NextPayment')?.content,
      render: (record: AgreementDisplayRow) => (
        <Typography as="span" variant="contentSmall" align="left">
          {record.nextPayment}
        </Typography>
      )
    },
    {
      title: contentStore.cmsStore.getContent('DiscountAgreement')?.content,
      render: (record: AgreementDisplayRow) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <Typography as="p" variant="contentSmallLink" align="left">
            {record.description.includes('ØresundGO')
              ? contentStore.cmsStore.getContent('DiscountAgreementName')?.content
              : contentStore.cmsStore.getContent('DiscountAgreementName')?.tooltip}
          </Typography>
          <Typography as="p" variant="contentSmall" color={ColorScheme.Grey6} align="left">
            {contentStore.cmsStore.getContent('DiscountAgreementName')?.subText}
          </Typography>
        </div>
      )
    },
    {
      title: '',
      render: () => null
    },
    {
      title: '',
      render: () => null
    },
    {
      title: contentStore.cmsStore.getContent('Price')?.content,
      align: 'right' as const,
      render: (record: AgreementDisplayRow) => (
        <Typography as="span" variant="contentSmall" align="right">
          {decimalFormatting(record.amount)} {record.currency}
        </Typography>
      )
    },
    {
      title: '',
      render: () => null
    }
  ];

  if (isMobileView) {
    return <AgreementsTableMobile contentStore={contentStore} rows={rows} isLoading={isLoading} />;
  }

  return (
    <StyledBrobizzTableForPrivate
      dataSource={rows}
      columns={agreementColumns as any}
      rowKey="key"
      pagination={false}
      loading={{
        spinning: isLoading,
        indicator: <LoadingSpinner />
      }}
      showSorterTooltip={false}
      locale={{
        emptyText: (
          <StyledText>{contentStore.cmsStore.getContent('NoAgreements')?.content || ''}</StyledText>
        )
      }}
    />
  );
};

export default observer(AgreementsTable);
